import { CheckCircleIcon } from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import { useForm } from "react-hook-form";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import UkModulusChecking from "uk-modulus-checking";
import { v4 as uuidv4 } from "uuid";
import { Actions as COMPANY_ACTIONS } from "../../../store/actions/companyActions";
const ibantools = require("ibantools");

const Bank = ({ updateCompanyDetails, organisationDetails, currentOrg, disabled, jurisdiction }) => {
	const { register, handleSubmit, watch, setValue, errors } = useForm();

	const bic = watch("bic", organisationDetails?.bankDetails?.[0]?.bic || "");

	const {
		id,
		name,
		iban,
		code,
		password,
		sortCode,
		accountNumber,
		accountHolder,
		buildingSocRef,
		custCode,
		fileRef,
	} = organisationDetails?.bankDetails?.[0] || [];

	const onSubmit = (data) => {
		const bankDetails = organisationDetails.bankDetails || [];

		switch (jurisdiction) {
			case "MT":
				const ibanCountry = data.iban.substr(0, 2);
				const bicCountry = data.bic
					.substr(4, 2)
					.replace(/IM|GG|JE/, "GB")
					.replace(/GF|GP|MQ|RE|PF|TF|YT|NC|BL|MF|PM|WF/, "FR");

				if (!data.iban || !ibantools.isValidIBAN(data.iban)) {
					NotificationManager.error("IBAN is Invalid", "Saving Bank Details");
					return;
				}

				if (!data.bic || !ibantools.isValidBIC(data.bic)) {
					NotificationManager.error("BIC is Invalid", "Saving Bank Details");
					return;
				}

				if (ibanCountry != bicCountry) {
					NotificationManager.error(`IBAN and BIC Country Codes Don't Match`, "Saving Bank Details");
					return;
				}
				break;

			case "UK":
				if (!new UkModulusChecking({ accountNumber: data.accountNumber, sortCode: data.sortCode }).isValid()) {
					NotificationManager.error(`Account Number or Sort Code is Invalid`, "Saving Bank Details");
					return;
				}
				break;
		}

		const detailsExist = bankDetails.findIndex((b) => b.id == id);
		if (detailsExist == -1) {
			bankDetails.push({
				id: uuidv4(),
				...data,
			});
		} else {
			bankDetails[detailsExist] = { ...bankDetails[detailsExist], ...data };
		}

		updateCompanyDetails(
			cleanDeep(
				{
					org: String(currentOrg),
					bankDetails,
				},
				{ emptyStrings: false, emptyArrays: false, emptyObjects: false },
			),
		);
	};

	return (
		<form
			className="px-8"
			autoComplete="off"
			data-id=""
			noValidate="novalidate"
			onSubmit={handleSubmit(onSubmit)}
		>
			<div className="col-lg-12 col-xs-12 form-section">
				<div className="form-container col-sm-12 m-0">
					<h4>Bank Details</h4>
				</div>
				<div className="col-xs-12">
					<p
						className="col-lg-12 col-xs-12 help-block"
						style={{ margin: 0, paddingBottom: 20, paddingLeft: 2 }}
					>
						Configure your worker's payment details. <br />
						Whether you pay by cheque or bank transfer, your details will be used to generate SEPA/BACS
						files or to set up payment integrations.
					</p>
				</div>

				{jurisdiction == "UK" && (
					<>
						<div className="col-sm-6 pr-0">
							<div className="form-material form-container">
								<input
									className="form-control"
									type="text"
									id="accountHolder"
									name="accountHolder"
									disabled={disabled}
									defaultValue={accountHolder}
									ref={register}
								/>
								<label htmlFor="accountHolder">Account Holder</label>

								<div className="clearfix" />
								<div className="clearfix" />
							</div>
						</div>
						<div className="col-sm-6 pr-0">
							<div className="form-material form-container">
								<input
									className="form-control"
									type="text"
									id="buildingSocRef"
									name="buildingSocRef"
									disabled={disabled}
									defaultValue={buildingSocRef}
									ref={register}
								/>
								<label htmlFor="buildingSocRef">Building Society Reference</label>

								<div className="clearfix" />
								<div className="clearfix" />
							</div>
						</div>
					</>
				)}
				<div className="clearfix" />

				<div className="col-sm-6 pr-0">
					<div className="form-material form-container">
						<input
							className="form-control"
							type="text"
							id="name"
							name="name"
							disabled={disabled}
							defaultValue={name}
							ref={register}
						/>
						<label htmlFor="bank_name">Bank Name</label>

						<div className="clearfix" />
						<div className="clearfix" />
					</div>
					<div className="clearfix" />
				</div>

				{jurisdiction == "MT" && (
					<>
						<div className="col-sm-6 pr-0">
							<div className="form-material form-container">
								<input
									className="form-control add-bank"
									type="text"
									id="bic"
									name="bic"
									ref={register}
									disabled={disabled}
									defaultValue={bic || ""}
								/>
								<label htmlFor="bic">BIC/Swift Code</label>
								{errors.bic && (
									<span style={{ display: "block", marginTop: 5 }}>{errors.bic.message}</span>
								)}
							</div>
						</div>

						<div className="col-sm-6 pr-0">
							<div className="form-material form-container">
								<input
									className="form-control add-bank"
									type="text"
									id="iban"
									name="iban"
									ref={register}
									disabled={disabled}
									defaultValue={iban || ""}
									onChange={(e) => {
										e.target.value = e.target.value.replace(/\s/g, "");
									}}
								/>
								<label htmlFor="iban">IBAN</label>
								{errors.iban && (
									<span style={{ display: "block", marginTop: 5 }}>{errors.iban.message}</span>
								)}
							</div>
						</div>
					</>
				)}

				{jurisdiction == "UK" && (
					<>
						<div className="col-sm-6 pr-0">
							<div className="form-material form-container">
								<input
									className="form-control add-bank"
									type="text"
									name="sortCode"
									ref={register}
									disabled={disabled}
									defaultValue={sortCode || ""}
								/>
								<label htmlFor="sortCode">Sort Code</label>
							</div>
						</div>

						<div className="col-sm-6 pr-0">
							<div className="form-material form-container">
								<input
									className="form-control add-bank"
									type="text"
									name="accountNumber"
									ref={register}
									disabled={disabled}
									defaultValue={accountNumber || ""}
								/>
								<label htmlFor="accountNumber">Account Number</label>
							</div>
						</div>
					</>
				)}

				<div className="col-sm-6 pr-0">
					<div className="form-material form-container">
						<input
							className="form-control"
							type="text"
							id="code"
							name="code"
							disabled={disabled}
							defaultValue={code}
							ref={register}
						/>
						<label htmlFor="code">Code / Initiator Id</label>
					</div>
				</div>

					{bic == "VALLMTMT" && (
						<div className="col-sm-6 pr-0">
							<div className="form-material form-container">
								<input
									className="form-control"
									type="password"
									autoComplete="off"
									id="password"
									name="password"
									disabled={disabled}
									defaultValue={password}
									ref={register}
								/>
								<label htmlFor="password">Password</label>
							</div>
						</div>
					)}
					{bic == "APSBMTMT" && jurisdiction == "MT" && (
						<>
							<div className="col-sm-6 pr-0">
								<div className="form-material form-container">
									<input
										className="form-control"
										type="text"
										id="custCode"
										name="custCode"
										disabled={disabled}
										defaultValue={custCode}
										ref={register}
										maxLength={3}
									/>
									<label htmlFor="code">APS Customer Code</label>
								</div>
							</div>
							<div className="col-sm-6 pr-0">
								<div className="form-material form-container">
									<input
										className="form-control"
										type="text"
										id="fileRef"
										name="fileRef"
										disabled={disabled}
										defaultValue={fileRef}
										ref={register}
									/>
									<label htmlFor="fileRef">APS File Reference</label>
								</div>
							</div>
						</>
					)}

				<div className="clearfix" />

				<div
					className="col-sm-12 col-lg-12"
					style={{ paddingBottom: 20 }}
				>
					<label className="css-input switch switch-sm switch-success">
						<input
							id="batch"
							disabled={disabled}
							name="batch"
							ref={register}
							type="checkbox"
							className="update-company-settings"
						/>
						<span />
						Effect Payments in Batch mode. Batch mode only shows 1 line in your bank statement.
					</label>
				</div>
			</div>

			<div className="clearfix" />
			<div
				className="col-xs-12 text-right submit-bank-btn"
				style={{ paddingRight: 0 }}
			>
				{!disabled && (
					<button
						className="btn btn-default btn-success"
						style={{ marginRight: "5" }}
						type="submit"
					>
						<CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
						<div style={{ float: "left" }}>Save</div>
					</button>
				)}
			</div>
		</form>
	);
};

const mapStateToProps = (state) => {
	return {
		jurisdiction: state.company?.settings?.jurisdiction,
		organisationDetails: state.company.settings,
		currentOrg: state.company?.settings?.org,
		disabled:
			state?.permissions?.my?.permissions?.settings == "Read" || state?.company?.settings?.status == "Archived",
	};
};

const mapDispatchToProps = (dispatch) => ({
	updateCompanyDetails: (body) => dispatch(COMPANY_ACTIONS.updateCompanyDetails(body)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Bank);
